import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDives, getDive, getDiveProfile, getDivePhotos, createDive, getDiveSuggestions, getBuddies } from '../api';
import offlineCache from '../offlineCache';

const PRECACHE_COUNT = 20;

async function precacheRecentDives(dives) {
  // Sorteer op datum, neem de laatste 20
  const recent = [...dives]
    .sort((a, b) => new Date(b.dive_date) - new Date(a.dive_date))
    .slice(0, PRECACHE_COUNT);

  for (const dive of recent) {
    // Sla over als al gecachet
    if (offlineCache.get(offlineCache.KEYS.dive(dive.id))) continue;
    try {
      const [d, p, photos] = await Promise.all([
        getDive(dive.id),
        getDiveProfile(dive.id),
        getDivePhotos(dive.id),
      ]);
      offlineCache.set(offlineCache.KEYS.dive(dive.id), d);
      offlineCache.set(offlineCache.KEYS.profile(dive.id), p);
      offlineCache.set(offlineCache.KEYS.photos(dive.id), photos);
    } catch (e) {
      // Stil falen — geen internet of server error
      break;
    }
  }
}

// ── Hulpfuncties ──────────────────────────────────────────────────────────────

function fmtDuration(sec) {
  if (!sec) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}u ${m}m` : `${m}m`;
}

function gasLabel(o2) {
  if (!o2) return null;
  if (o2 > 21.5) return <span className="badge badge-nitrox">EAN{Math.round(o2)}</span>;
  return <span className="badge badge-air">Lucht</span>;
}

function getVal(d, col) {
  switch (col) {
    case 'dive_number': return d.dive_number ?? 0;
    case 'dive_date':   return String(d.dive_date || '');
    case 'dive_time':   return String(d.dive_time || '');
    case 'max_depth_m': return parseFloat(d.max_depth_m) || -1;
    case 'duration_sec':return parseInt(d.duration_sec)  || -1;
    case 'temp_min_c':  return parseFloat(d.temp_min_c)  ?? 999;
    default: return '';
  }
}

// ── Kolomkop met sort-indicator ───────────────────────────────────────────────

function SortTh({ col, label, sortCol, sortDir, onSort, style }) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        <span style={{ fontSize: 10, opacity: active ? 1 : 0.3, color: active ? 'var(--blue)' : 'inherit' }}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
        </span>
      </span>
    </th>
  );
}

// ── Hoofd component ───────────────────────────────────────────────────────────

export default function DiveList() {
  const [dives, setDives]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [sortCol, setSortCol] = useState('dive_date');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({ year: '', gas: '', buddy: '', edited: '' });
  const [hideShallow, setHideShallow] = useState(() => localStorage.getItem('hideShallow') === 'true');
  const [showNew,    setShowNew]     = useState(false);
  const [newForm,    setNewForm]     = useState({ dive_date: '', max_depth_m: '', duration_min: '', gas_o2_percent: '', dive_computer_id: '' });
  const [newSaving,  setNewSaving]   = useState(false);
  const [computers,    setComputers]    = useState([]);
  const [buddyObjects, setBuddyObjects] = useState([]);
  const navigate = useNavigate();

  const toggleHideShallow = () => setHideShallow(v => {
    localStorage.setItem('hideShallow', !v);
    return !v;
  });

  useEffect(() => {
    getDives()
      .then(data => {
        offlineCache.set(offlineCache.KEYS.DIVES, data);
        setDives(data);
        // Stilletjes de laatste 20 duiken precachen op de achtergrond
        precacheRecentDives(data);
      })
      .catch(() => {
        const cached = offlineCache.get(offlineCache.KEYS.DIVES);
        if (cached) setDives(cached);
      })
      .finally(() => setLoading(false));

    getBuddies().then(setBuddyObjects).catch(() => {});
  }, []);

  // Unieke waarden voor filterdrops
  const years   = useMemo(() => [...new Set(dives.map(d => String(d.dive_date || '').substring(0, 4)).filter(Boolean))].sort().reverse(), [dives]);
  const buddies = useMemo(() => [...new Set(dives.map(d => d.buddy).filter(Boolean))].sort(), [dives]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  // Filter + zoek
  const afterFilter = useMemo(() => dives.filter(d => {
    const q = search.toLowerCase();
    if (q && !(
      String(d.dive_number || '').includes(q) ||
      String(d.dive_date   || '').includes(q) ||
      (d.location_name || '').toLowerCase().includes(q) ||
      (d.buddy         || '').toLowerCase().includes(q)
    )) return false;

    if (filters.year   && String(d.dive_date || '').substring(0, 4) !== filters.year) return false;
    if (filters.buddy  && d.buddy !== filters.buddy) return false;
    if (filters.gas === 'nitrox' && !(d.gas_o2_percent > 21.5)) return false;
    if (filters.gas === 'lucht'  &&  (d.gas_o2_percent > 21.5)) return false;
    if (filters.edited === 'ja'  && !d.manually_edited) return false;
    if (filters.edited === 'nee' &&  d.manually_edited) return false;
    if (hideShallow && (parseFloat(d.max_depth_m) || 0) <= 5) return false;
    return true;
  }), [dives, search, filters, hideShallow]);

  // Sorteren
  const sorted = useMemo(() => [...afterFilter].sort((a, b) => {
    const va = getVal(a, sortCol);
    const vb = getVal(b, sortCol);
    const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return sortDir === 'asc' ? cmp : -cmp;
  }), [afterFilter, sortCol, sortDir]);

  const activeFilters = Object.values(filters).filter(Boolean).length;

  const handleNewDive = async (e) => {
    e.preventDefault();
    if (!newForm.dive_date) return;
    setNewSaving(true);
    try {
      const { id } = await createDive(newForm);
      navigate(`/dives/${id}`);
    } catch (err) {
      alert('Opslaan mislukt: ' + err.message);
    } finally {
      setNewSaving(false);
    }
  };

  const setNF = (k, v) => setNewForm(f => ({ ...f, [k]: v }));

  if (loading) return <div className="loading">Duiken laden…</div>;

  const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 };
  const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 14, background: 'var(--card)', color: 'var(--text)', width: '100%', boxSizing: 'border-box' };

  const dropStyle = {
    padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)',
    fontSize: 12, background: 'var(--card)', color: 'var(--text)', cursor: 'pointer',
  };
  const activeDropStyle = { ...dropStyle, borderColor: 'var(--blue)', color: 'var(--blue)', fontWeight: 600 };

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Duiken</h1>
          <p className="page-subtitle">
            {sorted.length !== dives.length
              ? `${sorted.length} van ${dives.length} duiken`
              : `${dives.length} duiken totaal`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            style={{ fontSize: 13, padding: '7px 14px' }}
            onClick={() => { setNewForm({ dive_date: new Date().toISOString().slice(0,10), max_depth_m: '', duration_min: '', gas_o2_percent: '', dive_computer_id: '' }); getDiveSuggestions().then(s => setComputers(s.computers || [])).catch(() => {}); setShowNew(true); }}
          >
            ➕ Nieuwe duik
          </button>
          <button
            className="btn btn-outline"
            style={{ fontSize: 13, padding: '7px 14px' }}
            onClick={async () => {
              const token = localStorage.getItem('dive_token');
              const res = await fetch('/api/export/pdf', { headers: { Authorization: `Bearer ${token}` } });
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'dive-logbook.pdf'; a.click();
              URL.revokeObjectURL(url);
            }}
          >
            📄 Download PDF
          </button>
          <input
            type="search"
            placeholder="Zoeken…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', width: 200, fontSize: 13 }}
          />
        </div>
      </div>

      {/* Filterbalk */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Filter:</span>

        <select value={filters.year} onChange={e => setFilter('year', e.target.value)}
          style={filters.year ? activeDropStyle : dropStyle}>
          <option value="">Alle jaren</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select value={filters.gas} onChange={e => setFilter('gas', e.target.value)}
          style={filters.gas ? activeDropStyle : dropStyle}>
          <option value="">Alle gassen</option>
          <option value="lucht">Lucht</option>
          <option value="nitrox">Nitrox</option>
        </select>

        <select value={filters.buddy} onChange={e => setFilter('buddy', e.target.value)}
          style={filters.buddy ? activeDropStyle : dropStyle}>
          <option value="">Alle buddies</option>
          {buddies.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        <select value={filters.edited} onChange={e => setFilter('edited', e.target.value)}
          style={filters.edited ? activeDropStyle : dropStyle}>
          <option value="">Alle records</option>
          <option value="ja">✅ Aangevuld</option>
          <option value="nee">○ Nog te doen</option>
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: hideShallow ? 'var(--blue)' : 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={hideShallow}
            onChange={toggleHideShallow}
            style={{ accentColor: 'var(--blue)', width: 14, height: 14 }}
          />
          Verberg ≤ 5m
        </label>

        {(activeFilters > 0 || search) && (
          <button
            onClick={() => { setFilters({ year: '', gas: '', buddy: '', edited: '' }); setSearch(''); }}
            style={{ fontSize: 12, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px' }}
          >
            ✕ Wis filters
          </button>
        )}
      </div>

      {/* Tabel */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table className="dive-table">
            <thead>
              <tr>
                <SortTh col="dive_number"  label="#"          sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="dive_date"    label="Datum"      sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="dive_time"    label="Tijd"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="max_depth_m"  label="Max diepte" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="duration_sec" label="Duur"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortTh col="temp_min_c"   label="Temp"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <th>Gas</th>
                <th>Locatie</th>
                <th>Buddy</th>
                <th title="Manueel aangevuld">✏️</th>
                <th title="Foto's">📷</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Geen duiken gevonden</td></tr>
              )}
              {sorted.map(d => (
                <tr key={d.id} onClick={() => navigate(`/dives/${d.id}`)}>
                  <td><strong>{d.dive_number || '—'}</strong></td>
                  <td>{d.dive_date ? String(d.dive_date).substring(0, 10) : '—'}</td>
                  <td>{d.dive_time ? d.dive_time.substring(0, 5) : '—'}</td>
                  <td><strong>{d.max_depth_m ?? '—'}m</strong></td>
                  <td>{fmtDuration(d.duration_sec)}</td>
                  <td>{d.temp_min_c != null ? `${d.temp_min_c}°C` : '—'}</td>
                  <td>{gasLabel(d.gas_o2_percent)}</td>
                  <td>
                    {(d.location_name || d.site_name) ? (
                      <span
                        onClick={d.latitude && d.longitude ? e => {
                          e.stopPropagation();
                          navigate(`/map?lat=${d.latitude}&lon=${d.longitude}&id=${d.id}`);
                        } : undefined}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          color: 'var(--blue)', fontWeight: 600,
                          cursor: d.latitude && d.longitude ? 'pointer' : 'default',
                          background: 'var(--blue-light)', borderRadius: 6,
                          padding: '2px 8px', fontSize: 12, whiteSpace: 'nowrap',
                        }}
                      >
                        {d.latitude && d.longitude ? '📍' : '📌'} {d.location_name || d.site_name}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>
                  <td>{(() => {
                    if (!d.buddy) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                    const names = d.buddy.split(',').map(s => s.trim()).filter(Boolean);
                    const first = names[0];
                    const firstObj = buddyObjects.find(b => b.name.trim().toLowerCase() === first.toLowerCase());
                    const avatar = firstObj?.photo
                      ? <img src={`/uploads/buddies/${firstObj.photo}.jpg`} alt={first}
                          style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', verticalAlign: 'middle', border: '1px solid var(--border)' }} />
                      : <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: '50%', background: 'var(--blue-light)',
                          color: 'var(--blue)', fontSize: 13, fontWeight: 700, verticalAlign: 'middle',
                          border: '1px solid var(--border)',
                        }}>{first.charAt(0).toUpperCase()}</span>;

                    if (names.length === 1) {
                      return <span title={first} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {avatar}
                        {!firstObj?.photo && <span style={{ fontSize: 13 }}>{first}</span>}
                      </span>;
                    }
                    return <span title={names.join(', ')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'default' }}>
                      {avatar}
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>👥 {names.length}</span>
                    </span>;
                  })()}</td>
                  <td style={{ textAlign: 'center' }}>
                    {d.manually_edited
                      ? <span title="Manueel aangevuld" style={{ fontSize: 14 }}>✅</span>
                      : <span title="Nog niet aangevuld" style={{ fontSize: 14, opacity: 0.25 }}>○</span>
                    }
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {d.photo_count > 0
                      ? <span title={`${d.photo_count} foto${d.photo_count > 1 ? "'s" : ''}`} style={{ fontSize: 14 }}>📷 {d.photo_count}</span>
                      : <span style={{ fontSize: 14, opacity: 0.2 }}>—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Nieuwe duik modal */}
      {showNew && (
        <div onClick={() => setShowNew(false)} style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--surface)', borderRadius: 14, width: '100%', maxWidth: 520,
            boxShadow: '0 20px 60px rgba(0,0,0,.4)', maxHeight: '90vh', overflowY: 'auto',
          }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>🤿 Nieuwe duik toevoegen</h2>
              <button onClick={() => setShowNew(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
            </div>

            <form onSubmit={handleNewDive} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Datum */}
              <label style={labelStyle}>
                DATUM *
                <input type="date" required value={newForm.dive_date} onChange={e => setNF('dive_date', e.target.value)} style={inputStyle} />
              </label>

              {/* Diepte + Duur */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={labelStyle}>
                  MAX. DIEPTE (m)
                  <input type="number" step="0.1" min="0" placeholder="28.4" value={newForm.max_depth_m} onChange={e => setNF('max_depth_m', e.target.value)} style={inputStyle} />
                </label>
                <label style={labelStyle}>
                  DUUR (min)
                  <input type="number" min="1" placeholder="45" value={newForm.duration_min} onChange={e => setNF('duration_min', e.target.value)} style={inputStyle} />
                </label>
              </div>

              {/* Gas */}
              <label style={labelStyle}>
                GAS
                <select value={newForm.gas_o2_percent} onChange={e => setNF('gas_o2_percent', e.target.value)} style={inputStyle}>
                  <option value="">— Onbekend —</option>
                  <option value="21">Lucht (21%)</option>
                  <option value="32">Nitrox EAN32</option>
                  <option value="36">Nitrox EAN36</option>
                  <option value="40">Nitrox EAN40</option>
                </select>
              </label>

              {/* Computer */}
              <label style={labelStyle}>
                DUIKCOMPUTER
                <select value={newForm.dive_computer_id} onChange={e => setNF('dive_computer_id', e.target.value)} style={inputStyle}>
                  <option value="">— Geen / Onbekend —</option>
                  {computers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>

              {/* Knoppen */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                <button type="button" onClick={() => setShowNew(false)}
                  style={{ padding: '9px 18px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'none', color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer' }}>
                  Annuleren
                </button>
                <button type="submit" disabled={!newForm.dive_date || newSaving}
                  style={{
                    padding: '9px 22px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    background: newForm.dive_date && !newSaving ? 'var(--blue)' : 'var(--border)',
                    color: newForm.dive_date && !newSaving ? '#fff' : 'var(--text-muted)',
                  }}>
                  {newSaving ? '⏳ Opslaan…' : '✅ Duik opslaan'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
}
